import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { Toaster, toast } from "sonner";

import UnlockedView from "@/components/UnlockedView";
import QuestionsView from "@/components/QuestionsView";
import TectorBridge from "@/components/TectorBridge";
import OverallFeedback from "@/components/OverallFeedback";
import EngineAnimation from "@/components/EngineAnimation";
import { getCase } from "@/lib/api";

/**
 * Persistent read-only view for a case. Handles three states:
 *   1) locked (never unlocked)   -> show question list + unlock hint
 *   2) unlocked & pipeline ready -> full UnlockedView + Tector bridge + feedback
 *   3) unlocked but running      -> engine animation + poll (used for rerun-in-progress)
 */
export default function ReportView() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef({ mounted: true, timeoutId: null });

  const load = async () => {
    try {
      const d = await getCase(caseId);
      if (!pollRef.current.mounted) return;
      setData(d);
      // Keep polling while pipeline is running.
      const stillRunning =
        d.questions_status !== "ready" || d.top_answers_status !== "ready";
      if (stillRunning) {
        pollRef.current.timeoutId = setTimeout(load, 3500);
      }
    } catch (e) {
      if (!pollRef.current.mounted) return;
      setError(e?.response?.data?.detail || e.message || "Report not found.");
    } finally {
      if (pollRef.current.mounted) setLoading(false);
    }
  };

  useEffect(() => {
    pollRef.current = { mounted: true, timeoutId: null };
    setData(null);
    setError(null);
    setLoading(true);
    load();
    return () => {
      pollRef.current.mounted = false;
      if (pollRef.current.timeoutId) clearTimeout(pollRef.current.timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const questionsReady = data?.questions_status === "ready";
  const answersReady = data?.top_answers_status === "ready";
  const pipelineRunning =
    data && data.unlocked && (!questionsReady || !answersReady);

  const onRerunStarted = (newCaseId) => {
    toast.success("New rerun started. Redirecting…");
    // Navigate to the new report; pipeline polling handles the wait screen.
    navigate(`/report/${newCaseId}`);
  };

  return (
    <div className="min-h-screen bg-[#0B0F10] text-white">
      <Toaster theme="dark" position="top-right" />
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B0F10]/70 border-b border-white/5">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="report-home-link"
            className="flex items-center gap-2.5"
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#35D07F]/15 border border-[#35D07F]/40">
              <span className="h-2 w-2 rounded-sm bg-[#35D07F]" />
            </span>
            <div>
              <div className="font-bold tracking-tight text-white leading-none">
                ScopeGrid
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">
                by Tector · shared report
              </div>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="report-new-case"
            className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Run your own case
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-10 sm:py-14">
        {loading && !data && (
          <div className="rounded-2xl border border-white/10 bg-[#12181A] p-8 flex items-center gap-3 text-white/70">
            <Loader2 className="h-4 w-4 animate-spin text-[#35D07F]" />
            Loading report…
          </div>
        )}

        {error && !loading && (
          <div
            className="rounded-2xl border border-[#FF5252]/40 bg-[#FF5252]/[0.06] p-6 text-[#FFB4B4]"
            data-testid="report-error"
          >
            {error}
          </div>
        )}

        {data && !error && pipelineRunning && (
          <div className="space-y-6" data-testid="report-running">
            {data.parent_case_id && (
              <div className="rounded-2xl border border-white/10 bg-[#12181A] px-6 py-4 text-sm text-[#A0AAB2] flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#35D07F]" />
                Rerunning the engine with your new answers. About 90 seconds.
              </div>
            )}
            <EngineAnimation done={questionsReady} error={null} />
          </div>
        )}

        {data && !loading && !data.unlocked && !pipelineRunning && (
          <div className="space-y-6" data-testid="report-locked">
            <div className="rounded-2xl border border-white/10 bg-[#12181A] p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
                {data.display_name || "Report"}
              </div>
              <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-white tracking-tight">
                This report is still locked.
              </h1>
              <p className="mt-2 text-sm text-[#A0AAB2] max-w-2xl">
                The person who ran this case hasn't unlocked it yet. You can
                still see the question list below.
              </p>
              <Link
                to="/"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#35D07F] hover:bg-[#2CBB70] text-black text-sm font-semibold px-4 h-10"
              >
                Run your own case
              </Link>
            </div>
            {data.questions?.length > 0 && (
              <QuestionsView questions={data.questions} unlocked={false} />
            )}
          </div>
        )}

        {data && !loading && data.unlocked && !pipelineRunning && (
          <div className="space-y-10" data-testid="report-unlocked">
            <UnlockedView
              questions={data.questions}
              topAnswers={data.top_answers}
              caseId={data.case_id}
              problemType={data.problem_type}
              parentCaseId={data.parent_case_id}
              clarifyingQuestions={data.clarifying_questions}
              currentAnswers={data.answers}
              meta={{
                display_name: data.display_name,
                created_at: data.created_at,
              }}
              onRerunStarted={onRerunStarted}
            />
            <TectorBridge caseId={data.case_id} />
            <OverallFeedback
              caseId={data.case_id}
              problemType={data.problem_type}
            />
          </div>
        )}
      </main>

      <footer className="border-t border-white/5 mt-16">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center gap-3 justify-between text-xs text-white/40">
          <div>© {new Date().getFullYear()} Tector · ScopeGrid beta</div>
          <div>Shared report · axes stay server-side</div>
        </div>
      </footer>
    </div>
  );
}
